import React, { useState } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import { useTranslation } from 'react-i18next';
import WorkflowActionNode from '../WorkflowActionNode';
import ActionFlowHelp from './ActionFlowHelp';
import ConnectorPreparationMetrics from './ConnectorPreparationMetrics';
import useConnectorPreparationStep from './useConnectorPreparationStep';

const icons = { native: FilterAltOutlinedIcon, decide: AutoAwesomeOutlinedIcon, extract: DataObjectOutlinedIcon,
  condition: RuleOutlinedIcon, deduplicate: ContentCopyOutlinedIcon, transform: EditNoteOutlinedIcon, rank: SortRoundedIcon };

export default function ConnectorPreparationDagNode({ id, label, item, isEnabled, search, disableHover, canManage, controls, StatusIndicator }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const { state, busy, error } = useConnectorPreparationStep(item, hovered && canManage && !disableHover);
  const step = item.preparationStep;
  const Icon = icons[step.operation] || ExtensionOutlinedIcon;
  const pending = controls?.preparationPendingId === step.stepId;
  const canEdit = canManage && !pending && !disableHover && !step.native;
  const open = (tab) => controls?.onConfigureNode?.({ kind: 'preparation', item: { ...item, preparationInitialTab: tab } });
  const runtime = state?.entered ? { status: Number(state.counts?.review || 0) ? 'pending' : 'completed', completed: state.passed } : null;
  const tooltip = <Box sx={{ minWidth: 240, maxWidth: 300 }}>
    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{label}</Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 1, whiteSpace: 'pre-line' }}>
      {step.inputLabel} → {label} → {step.outputLabel}
    </Typography>
    {busy && <CircularProgress size={16} color="inherit" />}
    {error && <Typography variant="caption">{t(`connector_setup.errors.${error}`, { defaultValue: t('connector_setup.errors.setup_unavailable') })}</Typography>}
    <ConnectorPreparationMetrics step={step} state={state} compact />
    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>{t('connector_setup.step.open')}</Typography>
  </Box>;
  return <Box sx={{ position: 'relative', width: 154, '& > div': { mb: 0 } }}>
    <WorkflowActionNode setting={{ id, name: label, type: 'preparerecord' }} isEnabled={isEnabled}
      index={canEdit ? step.editableIndex : 0} totalSettings={canEdit ? step.editableCount : 1}
      status="idle" showEnableToggle={false} separateEnableToggle showNodePointer={false}
      showScopeIndicator={false} showWidgetBadge={false} canDelete={canEdit} solidNameBackground
      controlMenuTop={-30} dragHandleClassName="dag-action-node-drag-handle"
      onNameSave={canEdit ? (_id, name) => controls?.onPreparationCommand?.(item, 'rename', { name }) : undefined}
      onCopyAction={canEdit && step.operation !== 'rank' && step.editableCount < 16 ? () => controls?.onPreparationCommand?.(item, 'duplicate') : undefined}
      onDelete={canEdit ? () => controls?.onPreparationCommand?.(item, 'delete') : undefined}
      onConfigure={canManage && !pending ? () => open('rule') : undefined}
      onEditLogic={canManage && !pending ? () => open('rule') : undefined}
      onOpenData={canManage ? () => open('results') : undefined}
      nodeIcon={<Icon sx={{ fontSize: 42, color: '#5757b0', opacity: isEnabled ? 1 : 0.6 }} />}
      tooltipContent={tooltip} onTooltipOpen={() => setHovered(true)} onTooltipClose={() => setHovered(false)}
      disableHover={disableHover || pending} isSearchActive={search?.active} isSearchMatch={search?.match} />
    {StatusIndicator && <StatusIndicator label={label} runtime={runtime} t={t}
      disableHover={disableHover} onOpenJournal={() => open('results')} />}
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.3 }}>
      <Chip size="small" variant="outlined" label={t(item.preparationMode === 'review'
        ? 'connector_setup.pipeline.awaiting_activation' : 'connector_setup.pipeline.before_import')}
        sx={{ fontSize: 9, height: 20 }} />
      <ActionFlowHelp topic={label}>{[item.preparationHelp,
        t(step.native ? 'connector_setup.step.native_order_help' : 'connector_setup.step.drag_help')].filter(Boolean).join('\n\n')}</ActionFlowHelp>
    </Box>
  </Box>;
}
